import { createContext, useContext, ReactNode, useState } from 'react'
import { useTRPC, RouterOutputs } from '@/trpc'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { SYSTEM_COMPANY_ID } from '@tandem/schema/permissions'
import type { MemberRole } from '@tandem/schema/member'

type User = RouterOutputs['auth']['me']

export interface Company {
  company_id: number
  name: string
  type: string | null
  role: MemberRole
  permissions: Record<string, unknown> | null
  has_stores: boolean
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
  refetch: () => void
  company: Company | null
  companies: Company[]
  setActiveCompanyId: (id: number) => void
}

const ACTIVE_COMPANY_KEY = 'tandem:activeCompanyId'

let AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  let trpc = useTRPC()
  let queryClient = useQueryClient()

  let queryOptions = trpc.auth.me.queryOptions()
  let query = useQuery({
    queryKey: queryOptions.queryKey,
    queryFn: queryOptions.queryFn,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  let user = (query.data as User | undefined) ?? null

  let [activeId, setActiveId] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    let raw = localStorage.getItem(ACTIVE_COMPANY_KEY)
    return raw == null ? null : Number(raw)
  })

  // @ts-ignore deep trpc type
  let companies: Company[] = user?.companies ?? []

  let company: Company | null = null
  if (activeId != null) {
    company = companies.find((c) => c.company_id === activeId) ?? null
  }
  if (!company) {
    company =
      companies.find((c) => c.company_id !== SYSTEM_COMPANY_ID) ??
      companies[0] ??
      null
  }

  let setActiveCompanyId = (id: number) => {
    setActiveId(id)
    localStorage.setItem(ACTIVE_COMPANY_KEY, String(id))
  }

  let logoutMutation = useMutation(trpc.auth.logout.mutationOptions({
    onSuccess: () => {
      queryClient.setQueryData(trpc.auth.me.queryKey(), undefined)
      queryClient.clear()
      localStorage.removeItem(ACTIVE_COMPANY_KEY)
      setActiveId(null)
    },
  }))

  let logout = () => logoutMutation.mutate()

  return (
    <AuthContext.Provider value={{
      user,
      isLoading: query.isLoading,
      isAuthenticated: !!user,
      logout,
      refetch: query.refetch,
      company,
      companies,
      setActiveCompanyId,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  let context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
